import React, { useState } from "react";
import { useRulesStore } from "../../store/rulesStore";

interface EmailButtonProps {
  buttonText?: string;
  buttonVariant?: "primary" | "secondary" | "outline";
  buttonSize?: "sm" | "md" | "lg";
  className?: string;
  disabled?: boolean;
  onClick?: () => void;
  // Email configuration
  toEmails: string[];
  subject: string;
  body: string;
  htmlBody?: string;
  ccEmails?: string[];
  bccEmails?: string[];
  // PDF attachment support
  pdfAttachment?: {
    filename: string;
    content?: string; // base64 encoded PDF content (optional)
    contentType?: string;
  };
  // CSV attachment support
  csvAttachment?: {
    filename: string;
    content: string; // CSV content as string
    contentType?: string;
  };
  // Service configuration
  emailServiceUrl?: string;
  // Success/error callbacks
  onSuccess?: (response: unknown) => void;
  onError?: (error: unknown) => void;
  // Loading state
  showLoadingState?: boolean;
}

/**
 * Determines the PDF type based on active compliance rules
 * @param activeRules - Array of active rule instances
 * @returns PDF type string
 */
const getPDFType = (activeRules: any[]) => {
  const hasToiletRule = activeRules.some(
    (rule) => rule.templateId === "ada-toilet-side-wall-clearance"
  );
  const hasGrabBarRule = activeRules.some(
    (rule) => rule.templateId === "ada-side-wall-grab-bar"
  );
  const hasVisualAlarmRule = activeRules.some(
    (rule) => rule.templateId === "fire-alarm-visual-clearance"
  );

  // All 3 rules active
  if (hasToiletRule && hasGrabBarRule && hasVisualAlarmRule) {
    return "toilets-grab-bar-visual-alarms";
  }
  // Toilets + Grab Bars
  else if (hasToiletRule && hasGrabBarRule) {
    return "toilets-grab-bar";
  }
  // Toilets + Visual Alarms
  else if (hasToiletRule && hasVisualAlarmRule) {
    return "toilets-visual-alarms";
  }
  // Grab Bars + Visual Alarms
  else if (hasGrabBarRule && hasVisualAlarmRule) {
    return "grab-bar-visual-alarms";
  }
  // Individual rules
  else if (hasToiletRule) {
    return "toilets";
  } else if (hasGrabBarRule) {
    return "grab-bar";
  } else if (hasVisualAlarmRule) {
    return "visual-alarms";
  } else {
    return "compliance-report"; // Default fallback
  }
};

const EmailButton: React.FC<EmailButtonProps> = ({
  buttonText = "Send Email",
  buttonVariant = "primary",
  buttonSize = "md",
  className = "",
  disabled = false,
  onClick,
  toEmails,
  subject,
  body,
  htmlBody,
  ccEmails,
  bccEmails,
  pdfAttachment,
  csvAttachment,
  emailServiceUrl: propEmailServiceUrl,
  onSuccess,
  onError,
  showLoadingState = true,
}) => {
  // Get active rules from store
  const { getActiveRules } = useRulesStore();

  // Determine email service URL based on environment
  const emailServiceUrl = (() => {
    // If explicitly provided, use it
    if (propEmailServiceUrl) return propEmailServiceUrl;

    // Use environment variable if set (works for both dev and prod)
    if (import.meta.env.VITE_EMAIL_SERVICE_URL) {
      return import.meta.env.VITE_EMAIL_SERVICE_URL;
    }

    // Fallback: Check if we're in production (Vercel)
    if (
      typeof window !== "undefined" &&
      window.location.hostname !== "localhost" &&
      window.location.hostname !== "127.0.0.1"
    ) {
      return "https://email-service-erlz.onrender.com";
    }

    // Development fallback
    return "http://localhost:3001";
  })();

  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const getButtonClasses = () => {
    const baseClasses =
      "inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2";

    const sizeClasses = {
      sm: "px-3 py-1.5 text-sm",
      md: "px-4 py-2 text-base",
      lg: "px-6 py-3 text-lg",
    };

    const variantClasses = {
      primary: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500",
      secondary: "bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500",
      outline:
        "border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500",
    };

    const stateClasses =
      disabled || isLoading
        ? "opacity-50 cursor-not-allowed"
        : "cursor-pointer";

    return `${baseClasses} ${sizeClasses[buttonSize]} ${variantClasses[buttonVariant]} ${stateClasses} ${className}`;
  };

  const sendEmail = async () => {
    if (disabled || isLoading) return;

    setIsLoading(true);
    setIsSent(false);

    try {
      // Determine endpoint and request body based on attachment type
      let endpoint: string;
      const requestBody: any = {
        to: toEmails,
        subject,
        text: body,
        html: htmlBody,
        cc: ccEmails,
        bcc: bccEmails,
      };

      if (csvAttachment) {
        // Use CSV endpoint
        endpoint = `${emailServiceUrl}/api/send-email-with-csv`;
        
        // Convert CSV content to base64
        const csvBase64 = btoa(unescape(encodeURIComponent(csvAttachment.content)));
        
        requestBody.csvAttachment = {
          filename: csvAttachment.filename,
          content: csvBase64,
          contentType: csvAttachment.contentType || "text/csv",
        };
      } else if (pdfAttachment) {
        // Use PDF endpoint
        endpoint = `${emailServiceUrl}/api/send-email-with-pdf`;

        // Get active rules and determine PDF type
        const activeRules = getActiveRules();
        const pdfType = getPDFType(activeRules);
        requestBody.pdfType = pdfType;

        if (pdfAttachment.content) {
          requestBody.pdfAttachment = {
            filename: pdfAttachment.filename,
            content: pdfAttachment.content,
            contentType: pdfAttachment.contentType || "application/pdf",
          };
        } else {
          // Send PDF filename as parameter for email service to handle
          requestBody.pdfFilename = pdfAttachment.filename;
        }
      } else {
        // No attachment, use regular email endpoint
        endpoint = `${emailServiceUrl}/api/send-email`;
      }

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (result.success) {
        setIsSent(true);
        onSuccess?.(result);

        // Reset sent state after 3 seconds
        setTimeout(() => setIsSent(false), 3000);
      } else {
        throw new Error(result.error || "Failed to send email");
      }
    } catch (error) {
      onError?.(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      sendEmail();
    }
  };

  const getButtonContent = () => {
    if (isSent) {
      return (
        <>
          <svg
            className="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          Sent!
        </>
      );
    }

    if (isLoading && showLoadingState) {
      return (
        <>
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          Sending...
        </>
      );
    }

    return buttonText;
  };

  return (
    <button
      className={getButtonClasses()}
      onClick={handleClick}
      disabled={disabled || isLoading}
      type="button"
    >
      {getButtonContent()}
    </button>
  );
};

export default EmailButton;
